import { useMemo } from "react";
import { NOTIFICATION_COLUMN_FILTERS } from "../../constants";

/**
 * Picks only defined `NOTIFICATION_COLUMN_FILTERS` keys, trims string values, and omits empty strings.
 * @param {Record<string, unknown>} filters Raw filter object (draft or parent state).
 * @returns {Record<string, string>} Normalized map sent to the list query / parent.
 */
function normalizeFilters(filters) {
    const out = {};
    for (const { key } of NOTIFICATION_COLUMN_FILTERS) {
        const raw = filters?.[key];
        if (raw == null) {
            continue;
        }

        const value = String(raw).trim();

        if (value !== "") {
            out[key] = key === "type" ? value.toUpperCase() : value;
        }
    }

    return out;
}

/**
 * Select-value state helpers for column filters on the notifications queue.
 * Filters are stored as plain strings so URL params, query keys, and chips stay stable.
 *
 * @param {Record<string, string>} filters Applied filters from the parent (React Query key).
 * @param {(next: Record<string, string>) => void} onFiltersChange Parent callback; receives the same shape as `normalizeFilters()` return value.
 * @returns {{
 *   activeFilters: Record<string, string>,
 *   activeEntries: { key: string, label: string, value: string }[],
 *   hasActiveFilters: boolean,
 *   setFilterValue: (key: string, value: string | null | undefined) => void,
 *   removeFilter: (key: string) => void,
 *   clearAllFilters: () => void,
 * }}
 */
export function useColumnFilters(filters, onFiltersChange) {
    const activeFilters = useMemo(() => normalizeFilters(filters), [filters]);
    const activeEntries = NOTIFICATION_COLUMN_FILTERS.filter(
        ({ key }) => activeFilters[key],
    ).map(({ key, label }) => ({
        key,
        label,
        value: activeFilters[key],
    }));

    const applyFilters = (nextFilters) => {
        onFiltersChange(normalizeFilters(nextFilters));
    };

    const setFilterValue = (key, value) => {
        const next = { ...activeFilters };
        const trimmed = value == null ? "" : String(value).trim();

        if (trimmed === "") {
            delete next[key];
        } else {
            next[key] = trimmed;
        }

        applyFilters(next);
    };

    const removeFilter = (key) => {
        const next = { ...activeFilters };
        delete next[key];
        
        applyFilters(next);
    };

    const clearAllFilters = () => {
        applyFilters({});
    };

    const hasActiveFilters = activeEntries.length > 0;

    return {
        activeFilters,
        activeEntries,
        hasActiveFilters,
        setFilterValue,
        removeFilter,
        clearAllFilters,
    };
}
